import type { IconOwnProps } from '@ps/isp-web-component-library';
import { Icon } from '@ps/isp-web-component-library';
import { Finishingpost } from '@ps/isp-web-component-library/icons/Finishingpost';
import { Horse } from '@ps/isp-web-component-library/icons/Horse';
import { RaceStatus as RaceStatusType } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';

import {
  TXTWCL_Abandoned,
  TXTWCL_At_The_Post,
  TXTWCL_Delayed,
  TXTWCL_False_Start,
  TXTWCL_Finished,
  TXTWCL_Going_Behind,
  TXTWCL_Going_Down,
  TXTWCL_Off_2,
  TXTWCL_Parading,
  TXTWCL_Photograph_1,
  TXTWCL_Race_Void,
  TXTWCL_Result_3,
  TXTWCL_Under_Orders,
  TXTWCL_Weighed_In
} from './RaceStatus.messages.json';
import styles from './RaceStatus.scss';

const statusMap: Record<
  Exclude<RaceStatusType, RaceStatusType.DORMANT>,
  { icon?: IconOwnProps['icon']; className?: string; text: TranslationTag }
> = {
  [RaceStatusType.AT_THE_POST]: { icon: Horse, text: TXTWCL_At_The_Post },
  [RaceStatusType.DELAYED]: { icon: Horse, className: styles.delayed, text: TXTWCL_Delayed },
  [RaceStatusType.FALSE_START]: { icon: Horse, text: TXTWCL_False_Start },
  [RaceStatusType.FINISHED]: {
    icon: Finishingpost,
    className: styles.finished,
    text: TXTWCL_Finished
  },
  [RaceStatusType.GOING_BEHIND]: { icon: Horse, text: TXTWCL_Going_Behind },
  [RaceStatusType.GOING_DOWN]: { icon: Horse, text: TXTWCL_Going_Down },
  [RaceStatusType.OFF]: { icon: Horse, text: TXTWCL_Off_2 },
  [RaceStatusType.PARADING]: { icon: Horse, text: TXTWCL_Parading },
  [RaceStatusType.PHOTOGRAPH]: { icon: Horse, text: TXTWCL_Photograph_1 },
  [RaceStatusType.RESULT]: {
    icon: Finishingpost,
    className: styles.finished,
    text: TXTWCL_Result_3
  },
  [RaceStatusType.UNDER_ORDERS]: { icon: Horse, text: TXTWCL_Under_Orders },
  [RaceStatusType.WEIGHED_IN]: { icon: Horse, text: TXTWCL_Weighed_In },
  [RaceStatusType.RACE_VOID]: { text: TXTWCL_Race_Void, className: styles.raceVoid },
  [RaceStatusType.ABANDONED]: { text: TXTWCL_Abandoned, className: styles.abandoned }
};

interface Props {
  status: RaceStatusType;
  iconOnly?: true;
}

export const RaceStatus: React.FC<Props> = ({ status, iconOnly }) => {
  const { formatMessage } = useRegulatoryIntl();

  if (status === RaceStatusType.DORMANT) return null;

  const mappedStatus = statusMap[status];

  if (iconOnly && !mappedStatus.icon) return null;

  return (
    <div className={classNames(styles.raceStatus, mappedStatus.className)}>
      {mappedStatus.icon && (
        <Icon
          icon={mappedStatus.icon}
          label={iconOnly ? formatMessage(mappedStatus.text) : undefined}
          size="xl"
        />
      )}
      {!iconOnly && <Translation {...mappedStatus.text} />}
    </div>
  );
};

export default RaceStatus;
